import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import Sidebar from '../components/Sidebar';
import { Button } from '../components/ui/button';
import {
  Shield, FileText, Lock, CheckCircle2, Cookie, ChevronRight, Edit3, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';

const ICON_MAP = { shield: Shield, 'file-text': FileText, lock: Lock, 'check-circle': CheckCircle2, cookie: Cookie };

export default function PolicyCenter() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('Policies');
  const [policies, setPolicies] = useState([]);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [saving, setSaving] = useState(false);

  const loadPolicies = useCallback(async () => {
    try {
      const { data } = await api.get('/api/policies');
      setPolicies(data.policies || []);
      if (!selected && data.policies?.length > 0) setSelected(data.policies[0].slug);
    } catch (e) { /* handled */ }
  }, [selected]);

  useEffect(() => { loadPolicies(); }, [loadPolicies]);

  const currentPolicy = policies.find(p => p.slug === selected);
  const isAdmin = ['platform_admin', 'business_admin'].includes(user?.role);

  const startEdit = () => { setEditContent(currentPolicy?.content || ''); setEditing(true); };
  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.put(`/api/policies/${selected}`, { content: editContent, title: currentPolicy?.title });
      toast.success('Policy updated');
      setEditing(false);
      loadPolicies();
    } catch { toast.error('Save failed'); } finally { setSaving(false); }
  };

  // Simple markdown-ish renderer
  const renderContent = (text) => {
    if (!text) return null;
    return text.split('\n').map((line, i) => {
      if (line.startsWith('# ')) return <h1 key={`item-${i}`} className="text-xl font-bold text-gray-900 mt-6 mb-3">{line.slice(2)}</h1>;
      if (line.startsWith('## ')) return <h2 key={`item-${i}`} className="text-base font-bold text-gray-900 mt-5 mb-2">{line.slice(3)}</h2>;
      if (line.startsWith('### ')) return <h3 key={`item-${i}`} className="text-sm font-bold text-gray-800 mt-4 mb-1">{line.slice(4)}</h3>;
      if (line.startsWith('- **')) return <li key={`item-${i}`} className="text-xs text-gray-700 ml-4 mb-1" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(line.slice(2).replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')) }} />;
      if (line.startsWith('- ')) return <li key={`item-${i}`} className="text-xs text-gray-700 ml-4 mb-1">{line.slice(2)}</li>;
      if (line.startsWith('| ')) return <p key={`item-${i}`} className="text-xs text-gray-600 font-mono bg-gray-50 px-2 py-0.5">{line}</p>;
      if (line.startsWith('*') && line.endsWith('*')) return <p key={`item-${i}`} className="text-xs text-gray-500 italic">{line.replace(/\*/g, '')}</p>;
      if (line.trim() === '') return <div key={`item-${i}`} className="h-2" />;
      return <p key={`item-${i}`} className="text-xs text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')) }} />;
    });
  };

  return (
    <div className="flex min-h-screen bg-gray-50" data-testid="policy-center">
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />
      <main className="flex-1 p-6 md:p-8 lg:p-10 overflow-y-auto">
        <div className="space-y-6 animate-fade-in">
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900 flex items-center gap-2"><FileText className="w-6 h-6 text-[#D4A843]" /> Policy Center</h1>
            <p className="text-sm text-gray-500 mt-1">Legal policies, terms, and compliance documentation</p>
          </div>

          <div className="flex gap-6">
            {/* Policy Sidebar */}
            <div className="w-64 shrink-0 space-y-1" data-testid="policy-nav">
              {policies.map(p => {
                const Icon = ICON_MAP[p.icon] || FileText;
                return (
                  <button key={p.slug} onClick={() => { setSelected(p.slug); setEditing(false); }} className={`w-full text-left px-4 py-3 rounded-lg flex items-center gap-2.5 transition-colors ${selected === p.slug ? 'bg-[#D4A843] text-[#0A0A0A] font-semibold' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'}`} data-testid={`policy-${p.slug}`}>
                    <Icon className="w-4 h-4 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate">{p.title}</p>
                      <p className={`text-[9px] ${selected === p.slug ? 'text-blue-200' : 'text-gray-400'}`}>{p.last_updated}</p>
                    </div>
                    <ChevronRight className="w-3 h-3 shrink-0 opacity-50" />
                  </button>
                );
              })}
            </div>

            {/* Policy Content */}
            <div className="flex-1 bg-white border border-gray-200 rounded-lg p-8 min-h-[600px]" data-testid="policy-content">
              {currentPolicy ? (
                <>
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-lg font-bold text-gray-900">{currentPolicy.title}</h2>
                      <p className="text-[10px] text-gray-400 mt-0.5">Last updated: {currentPolicy.last_updated} {currentPolicy.is_default && <span className="text-blue-500">| Default template</span>}</p>
                    </div>
                    {isAdmin && !editing && (
                      <Button size="sm" variant="outline" onClick={startEdit} className="rounded-lg text-xs" data-testid="edit-policy"><Edit3 className="w-3.5 h-3.5 mr-1" /> Edit</Button>
                    )}
                  </div>
                  {editing ? (
                    <div className="space-y-3">
                      <textarea value={editContent} onChange={e => setEditContent(e.target.value)} className="w-full h-[500px] rounded-lg border border-gray-200 px-4 py-3 text-xs font-mono leading-relaxed" data-testid="policy-editor" />
                      <div className="flex gap-2">
                        <Button onClick={saveEdit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs" data-testid="save-policy">
                          {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />} Save Policy
                        </Button>
                        <Button variant="outline" onClick={() => setEditing(false)} className="rounded-lg text-xs">Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="prose prose-sm max-w-none">{renderContent(currentPolicy.content)}</div>
                  )}
                </>
              ) : (
                <div className="flex items-center justify-center h-full"><Loader2 className="w-5 h-5 animate-spin text-gray-300" /></div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
